import React, { useMemo, useEffect } from 'react';
import Image from 'frontastic/lib/image';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { mediumDesktop, tablet , mobile, smallMobile, hugeDesktop, largeDesktop, semiLargeDesktop} from 'helpers/utils/screensizes';
import Button from 'components/commercetools-ui/atoms/button';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import { Reference } from 'types/reference';
import Markdown from 'components/commercetools-ui/organisms/markdown';
import Breadcrumb from 'components/commercetools-ui/atoms/breadcrumb';
import { ChevronRightIcon } from '@heroicons/react/24/solid';
import { HomeIcon } from '@heroicons/react/24/outline';
import { useFormat } from 'helpers/hooks/useFormat';
import usePath from 'helpers/hooks/usePath';
import Slider from 'components/commercetools-ui/atoms/slider';
import useTouchDevice from 'helpers/hooks/useTouchDevice';
import CollectionCards from './collectioncards'
import { StaticImageData } from 'next/image';
import dynamic from 'next/dynamic';
import AdditionalInfo from '../../product-details/components/additional-info';
import { useParams, usePathname, useSearchParams } from 'next/navigation';
import { trackEvent } from 'helpers/customEvents';
import { useAccount } from 'frontastic';

export type CollectionProps = {
    data?: any;
    image: any;
    card_title: string;
    call_to_action?: any;
    summary?: string;
    card_description?: string;
    tag_line?: string;
};

const Collection = ({ data }: React.PropsWithChildren<CollectionProps>) => {

  const pathname = usePathname();
  const { loggedIn } = useAccount();
  const Markdown = dynamic(() => import('../../../markdown'));
  const [isDesktopSize] = useMediaQuery(mediumDesktop);
  const [isTabletSize] = useMediaQuery(tablet);
  const { path } = usePath();
  const [isMobileSize] = useMediaQuery(smallMobile);
  const [isHugeDesktopSize] = useMediaQuery(hugeDesktop);
  const [isSemiLargeDesktopSize] = useMediaQuery(semiLargeDesktop);
  const [ismediumDesktopSize] = useMediaQuery(mediumDesktop);
  const [isLargeDesktopSize] = useMediaQuery(largeDesktop);
      
  const sectionWithImageText = data?.sections?.find((section: { section_with_image_text: any; }) => section?.section_with_image_text)?.section_with_image_text;
  const sectionWithVideo = data?.sections?.find((section: { section_with_video: any; }) => section?.section_with_video)?.section_with_video;
  const sectionWithColumn = data?.sections?.find((section: { section_with_columns: any; }) => section?.section_with_columns)?.section_with_columns;
  const sectionWithMultiImages = data?.sections?.find((section: { multiple_image?: { section_with_images?: any; } }) => section?.multiple_image?.section_with_images)?.multiple_image?.section_with_images;
  const sectionWithHeadingDescription = data?.sections?.find((section: { section_with_heading_description: any; }) => section?.section_with_heading_description)?.section_with_heading_description;
  const sectionWithFeaturesBenifits = data?.sections?.find((section: { section_with_heading_description: any }) => section?.section_with_heading_description?.title === "Features & Benefits")?.section_with_heading_description;  
  const imageDescription = sectionWithImageText?.description;
  const {slug} = useParams();
  
  const descriptionArray: any[] = []
  descriptionArray?.push(imageDescription)
  const isClark = slug?.includes('clark');
  const isDorf = slug?.includes('dorf');
  const isInnovation = slug?.includes('innovation');

  useEffect(() => {
    if (slug) {
      trackEvent('collection_page_view', { collection_name: slug });
    }
  }, [slug, pathname, loggedIn]);

  const handleDownloadPDF = () => {
      const pdfUrl = sectionWithImageText?.ctas[0]?.button?.href;
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = 'your-filename.pdf';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
  };

  const generateBreadcrumbs = () => {
    if(isClark || isDorf){
      return(
        [
          <span key='1' style={{ cursor: 'default' }} className="text-14 font-normal capitalize leading-4 text-[#666]">About Us</span>,
          <span key='2' style={{ cursor: 'default' }} className="text-14 font-normal capitalize leading-4 text-[#666]">Our Other Brands</span>
        ]
      );
    }
    else if(isInnovation){
      return(
        <span key='1' style={{ cursor: 'default' }} className="text-14 font-normal capitalize leading-4 text-[#666]">About Us</span>
      );
    }
    else {
      return(
        <span key='1' style={{ cursor: 'default' }} className="text-14 font-normal capitalize leading-4 text-[#666]">Collections</span>
      );
    }
  };

  return (
    <>
      <div className="hidden px-24 pb-8 pt-16 md:block md:px-12 lg:px-32 xl:px-44 2xl:px-64">
        <Breadcrumb Separator={<ChevronRightIcon className="mt-2 w-16" />} listClassName="!gap-x-16">
          <Link link={'/'}>
            <HomeIcon className="mt-6 w-20" />
          </Link>
          {/* <span className="text-12 font-normal capitalize leading-4 text-[#666]">
            {path.split('/').slice(-2, -1)[0].replaceAll('-', ' ')} */}
          {/* <span style={{ cursor: 'default' }} className="text-14 font-normal capitalize leading-4 text-[#666]">
             {path.split('/').slice(-3, -1)[0].replaceAll('-', ' ')}
          </span> */}
          {generateBreadcrumbs()}
        </Breadcrumb>
      </div>
        <div className="relative  left-full  w-screen -translate-x-full lg:grid lg:grid-cols-12">
        <div
          id="advice-section"
          className={`flex items-center  lg:col-span-12 lg:px-0 ${
            sectionWithImageText?.desktop_image_alignment === 'Left' ? 'md:flex-row' : 'lg:flex-row-reverse'
          } ${sectionWithImageText?.mobile_image_alignment === 'Top' ? 'flex-col' : 'flex-col'}`}
        >
          <div className="relative  w-full xl:h-[32rem] md:h-[46rem] lg:flex-1 lg:flex-grow  h-[20rem] middleresolution:h-[37rem] 2xl:h-[45rem] ">
            {sectionWithImageText?.image?.url && (
                <Image priority loading='eager' src={sectionWithImageText?.image?.url} alt={sectionWithImageText?.image?.description} fill style={{ objectFit: 'cover' }} />
            )}
          </div>
          <div className="flex-1 flex-grow lg:px-48 2xl:px-144 pb-48">
            {sectionWithImageText?.title && (
                <Markdown markdown={sectionWithImageText.title} className="relative pb-10 text-32 pt-10 font-medium leading-[3rem]" />
            )}
            {descriptionArray&&
                descriptionArray?.map((field: any) => (
                  <div key={field?.categoryId}>
                    <Markdown
                      markdown={field}
                      className="image-container text-center px-24 text-16 md:text-18 xl:text-16 font-normal leading-6  text-[#333] lg:leading-8 2xl:text-20 [&>div>img]:mb-10"
                    />
                  </div>
                ))}
                {sectionWithImageText?.ctas?.[0]?.button && (
                <div className="px-24 ">
                    <Link link={sectionWithImageText?.ctas?.[0]?.button?.href?.href}>
                        <Button onClick={handleDownloadPDF} className="mt-48 block m-auto w-full rounded-none border-2 border-[#2B2C39] bg-white px-18 py-16 shadow-sm hover:!border-[#F4F4F4] hover:!bg-[#F4F4F4] md:px-32 md:py-16 lg:w-fit">
                            <Typography as="span" className="text-16 font-semibold leading-5  xl:text-14 2xl:text-16 lg:leading-6 text-[#090909]">
                                {sectionWithImageText?.ctas?.[0]?.button?.title}

                            </Typography>
                        </Button>
                    </Link>
                </div>
            )}
                  </div>
            </div>
          </div>
          <div className="">
            {sectionWithVideo && (
              <div className="video-container flex justify-center md:px-100 lg:py-85 2xl:px-208 ">
                    <iframe
                        src={sectionWithVideo?.video_link?.href}
                        width="100%"
                        loading="lazy"
                        onLoad={(e) => {
                          const iframe = e.currentTarget;
                          const src = iframe?.src || sectionWithVideo?.video_link?.href;
                          trackEvent('video_start', { video_url: src, video_type: 'embedded' });
                        }}
                        height={
                          isHugeDesktopSize ? '720px' :
                          isSemiLargeDesktopSize ? '480px' :
                          ismediumDesktopSize ? '500px' :
                          isLargeDesktopSize ? '600px' :
                          '320px'
                        }
                    ></iframe>
              </div>
            )}
          </div>
        <CollectionCards props={sectionWithColumn} />
        
        

        {/* ===========================================Home Care=========================================================== */}
        {sectionWithHeadingDescription && !sectionWithFeaturesBenifits &&(
          <div className="p-50">           
            <Markdown
              markdown={sectionWithHeadingDescription.title}
              className="mb-8 block text-28 font-medium leading-10 text-primary-black xl:text-36 2xl:text-48 lg:leading-[3.25rem] text-center"
            />
            <Markdown
              markdown={sectionWithHeadingDescription.description}
              className="text-20 font-normal leading-6 text-[#333] lg:leading-8 text-center w-full lg:w-[1700px] max-w-[calc(100%-16px)] lg:max-w-[calc(100%-90px)] 2xl:max-w-[calc(100%-208px)] px-16 md:px-90 2xl:px-208 py-24 2xl:py-40 mx-auto"
            />
          </div>
        )}
        {/* ===========================================Home Care=========================================================== */}




        {/* ===========================================Urbane Universal Accessories=========================================================== */}
        {/* New section added with heading and description */}
        {/* New section added with features and benifits */}
        {sectionWithHeadingDescription && sectionWithFeaturesBenifits &&(
          <div className="p-50">           
            <Markdown
              markdown={sectionWithHeadingDescription.title}
              className="mb-8 block text-28 font-medium leading-10 text-primary-black xl:text-36 2xl:text-48 lg:leading-[3.25rem] text-center"
            />
            <Markdown
              markdown={sectionWithHeadingDescription.description}
              className="text-20 font-normal leading-6 text-[#333] lg:leading-8 text-center w-full lg:w-[1700px] max-w-[calc(100%-16px)] lg:max-w-[calc(100%-90px)] 2xl:max-w-[calc(100%-208px)] px-16 md:px-90 2xl:px-208 py-24 2xl:py-40 mx-auto"
            />

            <Markdown
              markdown={sectionWithFeaturesBenifits?.title}
              className="mt-25 block text-20 font-medium leading-10 text-primary-black xl:text-36 2xl:text-38 lg:leading-[3.25rem] text-center"
            />
           <Markdown 
            markdown={sectionWithFeaturesBenifits?.description}
            className="
              text-[#333333] 
              markdown-container 
              font-normal 
              [&>p]:pt-12  

              // General width alignment and centering
              [&>ul]:w-[min(100%,47rem)] 
              [&>ul]:m-auto // Ensure lists are centered
              [&>ol]:w-[min(100%,47rem)] 
              [&>ol]:m-auto// Ensure ordered lists are centered
              [&>figure>div>figcaption]:w-[min(100%,47rem)] 
              [&>figure>div>figcaption]:m-auto// Ensure figure captions are centered

              [&>ul]:list-disc 
              [&>ul>li]:my-10  
              [&>ul>li>p>a]:!text-[#337ab7] 
              [&>p>a]:!text-[#337ab7] 
              [&>a]:!text-[#337ab7] 

              // Font size adjustments for tablet screens
              text-14 
              md:text-16 
              lg:text-16 
              xl:text-16 
              2xl:text-18

              text-16 font-normal leading-6 text-[#333] lg:leading-8 w-full lg:w-[1700px] 
              max-w-[calc(100%-16px)] lg:max-w-[calc(100%-90px)] 2xl:max-w-[calc(100%-208px)] 
              px-16 md:px-90 2xl:px-208 py-24 2xl:py-40 mx-auto
            "
          />
          </div>
        )}
        {/* ===========================================Urbane Universal Accessories=========================================================== */}




          {sectionWithMultiImages && (
            <div className="hero-slider-container w-full">
                <Slider
                    slidesPerView={isTabletSize ? 1.3 : 1}
                    dots={true}
                    spaceBetween={16}
                    arrows={isTabletSize}
                    innerArrows
                    allowArrowsOnTouchDevice
                    allowTouchMove={true}
                    prevButtonStyles={{ padding: '15px', left: '0px' }}
                    nextButtonStyles={{ padding: '15px', right: '0px' }}
                    autoplay={{
                        delay: 2000,
                        disableOnInteraction: false,
                    }}
                >
                {sectionWithMultiImages.image.map((slide: { url: string | StaticImageData | undefined; title: string | undefined; }, index: string | number | null | undefined) => (
                <div key={index} className="relative h-[18rem] md:h-[34rem] 2xl:h-[44rem]">
                  {slide?.url && (
                    <Image
                      loading='lazy'
                      src={slide.url}
                      alt={slide.title}
                      fill
                      style={{ objectFit: 'cover' }}
                    />
                  )}
                  </div>
              ))} 
              </Slider> 
            </div> 
          )}
  </>
  );
};

export default Collection;

